import React, { useEffect, useReducer } from "react";
import { Box, Typography, Divider, CircularProgress } from "@mui/material";
import { IframeCardsGrid } from "components/common/custom-component/IframeCardsGrid";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { fetchDashboardsThunk } from "store/thunks/dashboardThunks";
import { logger } from "helpers/logger";
import { dashboardPageReducer, initialState } from "reducers/dashboardPageReducer";
import CalendarIcon from "@mui/icons-material/CalendarToday";

const DashboardGridPage = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const [state, dispatchState] = useReducer(dashboardPageReducer, initialState);
  const { items } = state.data;
  const { isLoadingBackdrop: loading } = state.loading;
  const { error } = state;

  useEffect(() => {
    let active = true;
    const loadDashboards = async () => {
      try {
        dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
        dispatchState({ type: "SET_ERROR", payload: null });

        const data = await dispatch(fetchDashboardsThunk()).unwrap();
        logger.info("📊 Fetched dashboards payload:", data);

        const dashboards = data?.data?.dashboards || data?.dashboards || [];

        if (active) {
          const mappedItems = dashboards.map((dashboard, index) => ({
            id: index + 1,
            title: dashboard?.name || `${t("Dashboard")} ${index + 1}`,
            url: dashboard?.url || "",
          }));
          dispatchState({ type: "SET_ITEMS", payload: mappedItems });
        }
      } catch (err) {
        if (err !== "cancelled" && active) {
          logger.error("❌ Error loading dashboards:", err);
          dispatchState({ type: "SET_ERROR", payload: err || t("Failed to load dashboards") });
        }
      } finally {
        if (active) {
          dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
        }
      }
    };

    loadDashboards();

    return () => {
      active = false;
    };
  }, [dispatch, t]);

  return (
    <Box sx={{ p: 2, pb: 12, width: "100%", height: "calc(100vh - 50px)", overflow: "hidden" }}>
      <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
        {t("Command & Control Center")}
      </Typography>

      <Typography variant="body1" color="text.secondary">
        {t("Dashboards")}
      </Typography>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ flexGrow: 1, height: "100%", mt: 0.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
            <Typography color="error" variant="h6">
              {error}
            </Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, width: "100%" }}>
            <CalendarIcon
              sx={{
                fontSize: 64,
                color: "text.secondary",
                mb: 2
              }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t("No dashboards found.")}
            </Typography>
          </Box>
        ) : (
          <IframeCardsGrid items={items} />
        )}
      </Box>
    </Box>
  );
};

export default DashboardGridPage;
